<script setup lang="ts">
import { ref, computed } from 'vue';
import adminAPI from '@/services/api';
import { computedAsync } from '@vueuse/core';
import draggable from 'vuedraggable'
import { formatUTCToLocalTime } from "@/utils/date";

import { PaperClipIcon, InformationCircleIcon, DocumentPlusIcon, DocumentMagnifyingGlassIcon, TrashIcon, CloudIcon }
  from '@heroicons/vue/24/outline';
  import { RadioGroup, RadioGroupLabel, RadioGroupOption, RadioGroupDescription } from '@headlessui/vue'


import BaseButton from '../ui/buttons/BaseButton.vue';
import { useClaimStore, useNotificationStore } from '@/store';
import { RetrieveClaimWithRelationsResponse, SearchClaimCollection } from '@upclaim/sdk/src/data-contracts';
import DotLegalStep from '../ui/badges/DotLegalStep.vue';
import { BoxesIcon } from 'lucide-vue-next';
import { GripVerticalIcon } from 'lucide-vue-next';
import { UngroupIcon } from 'lucide-vue-next';
import BaseInput from '../ui/inputs/BaseInput.vue';
import { PencilLineIcon } from 'lucide-vue-next';
import { CombineIcon } from 'lucide-vue-next';

const drag = ref(false);
const transitionOptions = computed(() => { return { animation: 200, disabled: false, ghostClass: "ghost", }; });

const emptyGroup = {
  groupName: "",
  claims: [],
};
const newGroup = ref(emptyGroup);

async function createGroup() {
  const result = await adminAPI.upsertClaimGroup({
    groupName: newGroup.value.groupName,
    claimsIds: newGroup.value.claims.map(({ id }) => id)
  });
  useNotificationStore().emitNotification({ title: "Groupe créé avec succès", type: "success" });
  newGroup.value = emptyGroup;
  console.log("created group:", result);
}
</script>
<template>
<div class="p-4">

    <!-- <div class="flex items-center justify-between">
      <h2 class="text-sm font-medium leading-6 text-gray-900">
      </h2>
      <a href="#" class="text-sm font-medium leading-6 text-indigo-600 hover:text-indigo-500">See performance specs</a>
    </div> -->

    <RadioGroup class="mt-2">
      <RadioGroupLabel class="sr-only">Groupe</RadioGroupLabel>
        <draggable group="claims"
          v-bind="transitionOptions"
          @start="drag = true"
          @end="drag = false"
          :list="newGroup.claims" item-key="id" role="list"
          class="w-full p-4 space-y-4 bg-white rounded-md shadow-sm"
          :class="{ 'border border-gray-300 divide-y divide-gray-100': newGroup.claims.length }"
        >

          <template #item="{ element, index }: { element: SearchClaimCollection[number], index: number }">
            <div class="text-left">
              <RadioGroupOption as="template" :key="element.id" :value="element" v-slot="{ active, checked }">
              <div :class="[active ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-gray-300', 'relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between']">
                <div>
                  <div class="flex items-center">
                    <div class="flex-shrink-0 w-10 h-10">
                      <img class="w-10 h-10 rounded-full" :src=" element.effectiveAirlineIconURL "
                        alt="Airline Logo" />
                    </div>
                    <div class="ml-4 divide-y-2 divide-transparent">
                      <div class="font-medium text-gray-900">{{ element.effectiveFlightIdentifier }} · {{
                        element.effectiveAirlineName
                        }}</div>
                      <div class="font-medium text-slate-700">
                        Vol {{ element.departureAirportIATA }} → {{ element.arrivalAirportIATA }}
                        (du {{ formatUTCToLocalTime(new Date(element.flightScheduledDepartureUTC), element.departureAirportTimezone, "dd/MM/yyyy") }})
                      </div>
                      <div v-if="element.travelAgencyCompany">
                        <span class="mr-2 text-slate-700">
                          Entreprise : {{
                            // @ts-ignore
                            element.travelAgencyCompany?.displayName
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <span class="flex items-center">
                  <span class="flex flex-col text-sm">
                    <RadioGroupLabel as="span" class="font-medium text-gray-900">
                      {{ element.passengers[0].firstName }}
                      <span class="uppercase">{{ element.passengers[0].lastName }}</span>
                      <span v-if="element.passengers.length > 1" v-tooltip.right="{
                        html: true,
                        content: element.passengers.filter((_, i) => i)
                          .map(p => `${p.firstName} ${p.lastName.toUpperCase()}`).join('<br>'),
                      }">
                        (+ {{ element.passengers.length - 1 }}pax.)
                      </span>
                    </RadioGroupLabel>
                    <RadioGroupDescription as="span" class="text-gray-500">
                      <span class="inline-flex items-center mt-1 text-xs">
                        <DotLegalStep :legalStepName="element.claimLegalStepName" />
                        <span class="block min-w-full ml-1 sm:inline">{{ element.claimStateDisplayName }}</span>
                      </span>
                      <!-- {{ ' ' }}
                      <span class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                      {{ ' ' }}
                      <span class="block sm:inline">{{ element.effectiveAirlineName }}</span> -->
                    </RadioGroupDescription>
                  </span>
                </span>
                <RadioGroupDescription as="span" class="flex mt-2 text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                  <span class="font-medium text-gray-900">#{{ element.ticketNumber }}</span>
                  <!-- <span class="ml-1 text-gray-500 sm:ml-0">/mo</span> -->
                </RadioGroupDescription>
                <span :class="[active ? 'border' : 'border-2', checked ? 'border-indigo-600' : 'border-transparent', 'pointer-events-none absolute -inset-px rounded-lg']" aria-hidden="true" />
              </div>
            </RadioGroupOption>
              <!-- <div class="flex items-center flex-1 w-0 cursor-pointer" @click="previewDocument(element.file)">
                <PaperClipIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                <div class="flex flex-1 min-w-0 gap-2 ml-4">
                  <span class="font-medium truncate">
                    {{ element.documentType.type === "OTHER" ? element.file.name : typeTranslations[element.documentType.type] }}
                  </span>
                </div>
              </div>
              <div class="flex flex-shrink-0 ml-4 space-x-4" v-permission="['UPCLAIM_ADMIN', 'LAWYER']">
                <button type="button" v-tooltip="tooltipFile(element as any)" @click="previewDocument(element.file)"
                  class="font-medium text-blue-600 rounded-md hover:text-blue-500">
                  <DocumentMagnifyingGlassIcon class="w-5 h-5" />
                </button>
                <span class="text-gray-200" aria-hidden="true">|</span>
                <button type="button" class="font-medium text-gray-900 rounded-md hover:text-gray-800"
                  @click="removeClaimDocument(element.id)"
                >
                  <TrashIcon class="w-5 h-5" />
                </button>
              </div> -->
            </div>
          </template>

          <template #footer>
            <div v-if="!newGroup.claims?.length" role="list" class="w-full col-span-3 mb-5 bg-white border border-gray-300 border-dotted divide-y divide-gray-100 rounded-md shadow-sm">
              <div  class="flex items-center justify-between py-12 pl-4 pr-5 text-sm leading-6">
                <div class="flex items-center flex-1 w-0">
                  <UngroupIcon class="flex-shrink-0 w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />
                  <div class="flex flex-1 min-w-0 gap-2 ml-4">
                    <span class="flex items-center font-medium truncate">
                      Créer un nouveau groupe
                      <span class="hidden sm:mx-2 sm:inline" aria-hidden="true">&middot;</span>
                      {{ ' ' }}
                      Glisser-déposer ici
                    </span>
                    <div class="ml-auto">
                      <!-- <BaseButton @click="modalAddDocument = true" text="Ajouter" /> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

        </draggable>
    </RadioGroup>

    <div v-if="newGroup.claims?.length">
      <div class="p-6">
        <BaseInput placeholder="Nom du groupe" v-model="newGroup.groupName" :icon="PencilLineIcon" />
      </div>
      <BaseButton
        @click="() => createGroup()"
        text="Créer un groupe"
        type="submit"
        :icon="(CombineIcon as any)"
      />
    </div>
</div>
</template>
